/** UI strings (Spanish, Argentine register). Every user-facing string lives here. */
export const es = {
  appName: 'Pibex 2026',
  loading: 'Cargando…',
  signIn: 'Ingresar con Google',
  signOut: 'Salir',
  admin: 'Admin',
  retry: 'Reintentar',
  cancel: 'Cancelar',
  confirm: 'Confirmar',
  genericError: 'Algo salió mal. Probá de nuevo en un momento.',
  networkError: 'No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.',

  login: {
    title: 'Ingresar',
    onlyInvited:
      'Solo pueden entrar las personas invitadas. Usá la cuenta de Google con la que te invitaron.',
    missingClientId:
      'Falta configurar VITE_GOOGLE_CLIENT_ID, así que el botón de Google no se puede mostrar en este entorno.',
    scriptError: 'No pudimos cargar el botón de Google. Recargá la página para intentar de nuevo.',
    signingIn: 'Verificando tu cuenta…',
    invalidToken: 'Google no pudo validar tu cuenta. Probá de nuevo.',
  },

  home: {
    greeting: (firstName: string) => `Hola, ${firstName}`,
    intro: 'Acá va a estar todo lo del viaje. Por ahora estamos armando las secciones.',
    comingSoon: 'Próximamente',
    sections: {
      itinerary: {
        title: 'Itinerario',
        description: 'Qué hacemos cada día, horarios y traslados.',
      },
      expenses: {
        title: 'Gastos',
        description: 'Quién pagó qué y cuánto le debe cada uno a quién.',
      },
      lodging: {
        title: 'Alojamiento',
        description: 'Dónde nos quedamos, direcciones y cómo llegar.',
      },
    },
    adminLink: 'Panel de admin',
  },

  notInvited: {
    title: 'No estás en la lista',
    body: (email: string | null) =>
      email
        ? `La cuenta ${email} todavía no fue invitada. Pedile a Eze que te sume.`
        : 'Esta cuenta de Google todavía no fue invitada. Pedile a Eze que te sume.',
    disabledTitle: 'Tu acceso está deshabilitado',
    disabledBody: 'Tu cuenta fue deshabilitada. Si creés que es un error, hablá con Eze.',
    tryAnother: 'Probar con otra cuenta',
  },

  notFound: {
    code: '404',
    title: 'Página no encontrada',
    body: 'La dirección que abriste no existe o ya no está disponible.',
    goHome: 'Volver al inicio',
  },

  adminPage: {
    title: 'Panel de admin',
    subtitle: 'Invitá gente y administrá quién puede entrar.',
    backHome: 'Volver al inicio',
    loadError: 'No pudimos cargar los datos del panel.',

    invite: {
      heading: 'Invitar por email',
      label: 'Email',
      placeholder: 'nombre@gmail.com',
      submit: 'Invitar',
      submitting: 'Enviando…',
      hint: 'Le va a llegar un mail con el link del sitio. Tiene que entrar con esa misma cuenta de Google.',
      success: (email: string) => `Listo: invitamos a ${email}.`,
      successNoEmail: (email: string) =>
        `Creamos la invitación para ${email}, pero el mail no salió. Podés reenviarlo desde la lista.`,
      conflict: 'Ese email ya tiene una invitación pendiente o ya es miembro.',
      validation: 'Ingresá un email válido.',
    },

    invitations: {
      heading: 'Invitaciones',
      empty: 'Todavía no invitaste a nadie.',
      status: { pending: 'Pendiente', accepted: 'Aceptada', revoked: 'Revocada' },
      sentAt: (when: string) => `Enviado ${when}`,
      sendError: (message: string) => `Error de envío: ${message}`,
      notSent: 'Todavía no se envió el mail.',
      acceptedAt: (when: string) => `Aceptada ${when}`,
      resend: 'Reenviar',
      resending: 'Reenviando…',
      resent: (email: string) => `Reenviamos la invitación a ${email}.`,
      revoke: 'Revocar',
      revokeConfirm: (email: string) => `¿Revocar la invitación de ${email}?`,
      revoked: (email: string) => `Revocamos la invitación de ${email}.`,
      conflict: 'Esa invitación ya no está pendiente.',
    },

    members: {
      heading: 'Miembros',
      empty: 'Todavía no entró nadie.',
      role: { admin: 'Admin', member: 'Miembro' },
      status: { active: 'Activo', disabled: 'Deshabilitado' },
      toggleLabel: (name: string) => `Acceso de ${name}`,
      adminLocked: 'El admin no se puede deshabilitar.',
      updated: (name: string, status: 'active' | 'disabled') =>
        status === 'active' ? `${name} vuelve a tener acceso.` : `${name} quedó deshabilitado.`,
      conflict: 'No se puede cambiar el estado de ese miembro.',
      lastLogin: (when: string) => `Último ingreso ${when}`,
      neverLoggedIn: 'Nunca ingresó',
    },
  },
} as const;
